import { Injectable } from '@angular/core';
import { RequestConfigImplementation, REQUEST_METHODS, XHR_STRATEGIES, HTTPWrapperContext } from '@ptl/xhr';
import { HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { BrandingConfigurationService } from '@ptl/brand';
import { SuccessResponse } from 'src/app/angular-elements/form-wrappers/form-components/required-docs-upload/file-display/interfaces/sxesvc.api.v1.review.document.response.interface';
import { AddNoteServicePayload, AppSearchFilters, BulkSubmitResponse, DocumentStore, FilterKeyValue, RowDataStructure, SearchSubmittedSepAppResponse, SepAppInfoStructure } from '../sep-application-review-grid/interfaces/sep-application-review-grid.interface';
import { ActionItem, ActionItemRendererParams, DetailObject, DetailValues } from 'src/app/angular-elements/shared/components/ag-grid-cell-renderers/interfaces/ag-grid-cell-renderer.interface';
import { GridApi, ICellRendererParams } from 'ag-grid-community';
import { UploadDocumentsModalComponent } from '../sep-application-review-grid/upload-documents-modal/upload-documents-modal.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { DetailNotesRendererParams } from 'src/app/angular-elements/shared/components/ag-grid-cell-renderers/ag-detail-notes-renderer/ag-detail-notes-renderer.component';
import { APPLICATION_ACTION_OPTIONS, FILTER_OPTS, RELATIONSHIP_TO_APPLICANT, SEP_REVIEW_DASHBOARD_COLUMN_NAME, STATUS_OPTION_SERVICE_VALUES } from '../sep-application-review-grid/enums/sep-application-review-grid.enum';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DisplayableFile, FILE_UPLOAD_STATUS } from 'src/app/angular-elements/form-wrappers/form-components/required-docs-upload/interfaces/displayable-file.interface';
import moment from 'moment';
import { IndividualMarketsEnrollmentSubmissionService } from 'src/app/angular-elements/shared/services/enrollment/individual-markets/enrollment-submission.individual-markets.service';
import { EnrollPayloadRequest } from 'src/app/angular-elements/shared/interfaces/enrollment-submission.interface';
import { ModalArgs } from '../sep-application-review-grid/interfaces/sep-application-review.document-upload-modal.interface';
import { PrintButtonService } from 'src/app/angular-elements/shared/components/print-button/services/print-button.service';
import { PRINT_TYPE } from 'src/app/angular-elements/shared/components/print-button/enums/print-type.enum';
import { DOCUMENT_UPLOAD_TYPES } from 'src/app/angular-elements/shared/components/configs/interfaces/life-event.state-structure.interface';
import { Subject } from 'rxjs';
import { SubmitAppResponse } from 'src/app/angular-elements/shared/services/enrollment/enrollment-submission.service';
import { APP_STATUS } from '../../enums/app-status.enum';
import { PtlNotificationService, PtlNotificationStyle } from '@ptl/global-components';
import { LOGIN_INFO_REDUCER_NAME, UserInfoStateStructure } from '@ptl/account-management';

@Injectable({
  providedIn: 'root'
})

export class SepApplicationReviewDashboardService {

  public gridApi
  public gridReady$: Subject<GridApi> = new Subject();

  constructor(
    private httpClient: HTTPWrapperContext,
    private brandService: BrandingConfigurationService,
    private matDialog: MatDialog,
    private reducerUtilityService: ReducerUtilityService,
    private matSnackBar: MatSnackBar,
    private individualMarketsEnrollmentSubmissionService: IndividualMarketsEnrollmentSubmissionService,
    private printButtonService: PrintButtonService,
    private ptlNotificationService: PtlNotificationService
  ){}

  syncGrid(gridApi: GridApi) {
    this.gridApi = gridApi;
    this.gridReady$.next(gridApi);
  }

  async fetchApplicationData(filterValues?: AppSearchFilters): Promise<SearchSubmittedSepAppResponse>{

    const serviceUrl = `/sxesvc/api/v1/apps/search`;

    const payload: {
      pageNo: number;
      pageSize: number;
      filters?: FilterKeyValue[];
      sort?: FilterKeyValue[];
    } = {
      pageNo: 1, 
      pageSize: 10000,
    }

    if(filterValues){
      if(filterValues?.sort){
        payload.sort = filterValues.sort;
      }
      if(filterValues?.filters){
        payload.filters = filterValues.filters;
      }
    }

    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded()
      }),
      url: serviceUrl,
      POST_OR_PUT_PAYLOAD: payload
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest<SearchSubmittedSepAppResponse>(requestConfig);
  }

  async fetchApplicationDataByAppId(appId: string): Promise<SearchSubmittedSepAppResponse> {
    
    const filter: AppSearchFilters = {
        filters: [
          { 
            key: FILTER_OPTS.APP_NO,
            value: appId 
          }
        ]
      };
    
    const response: SearchSubmittedSepAppResponse = await this.fetchApplicationData(filter);
    return response;
  }

  async fetchEnrollmentObjectByAppId(appId: string): Promise<EnrollPayloadRequest> {
    const applicationData: SearchSubmittedSepAppResponse = await this.fetchApplicationDataByAppId(appId);
    const enrollmentObject: EnrollPayloadRequest = JSON.parse(applicationData.data[0].enrollmentObject) as EnrollPayloadRequest;
    return enrollmentObject;
  }

  buildGridData(response: SepAppInfoStructure[]): RowDataStructure[]{
    let gridData: RowDataStructure[] = [];
    
    for(const app of response){
      const lastEdit = !app.notes?.length ? null : app.notes.at(-1);
      gridData.push({
        actionMenu: null,
        status: app.currentStatus,
        applicationNumber: app.applicationNumber,
        applicationDate: app.applicationSubmittedDate,
        subscriberName: `${app.subscriberFirstName} ${app.subscriberLastName}`,
        lifeEvent: app.lifeEvent,
        subscriberDob: app.dob,
        lastEdited: app.lastModifiedDate,
        lastEditedBy: !lastEdit ? app.agentInfo?.agentName : lastEdit.lastModifiedBy,
        documentActions: null,
        notes: this.getLastNote(app),
        detailDisplayObj: this.buildDetailDisplayObject(app),
        documents: this.getDocumentsArray(app),
        enrollmentObj: app.enrollmentObject
      })
    }
    return gridData;
  }
  
  getUpdatedServiceUrl(url: string): string{
    let curBrand = this.brandService.getCurrentBrand();
    let ddValue = curBrand.ddToken;
    let ubk = curBrand.ubk;

    if (ddValue && ubk) {
      const updatedUrl = url.trim()
        .replace('{ddValue}', ddValue)
        .replace('{ubk}', ubk);
      return updatedUrl;
    }
  }

  async viewApplicationDocumentService(response: SepAppInfoStructure[], appId: string): Promise<any>{
    const docId = this.gridApi.getRowNode(appId)?.data?.documents?.documentId ?? null;

    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.GET,
      url: `/sxesvc/api/v1/dcnupv/generate/${docId}`,
      contentType: 'application/octet-stream',
      accept: 'application/octet-stream',
      responseType: 'blob',
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded(),
      }),
    });

    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_PRE_PASS);
    return this.httpClient.makeRequest<HttpErrorResponse | SuccessResponse>(requestConfig).catch((fail) => fail);
  }

  openPdfInPrintWindow(blobData): void{
    let blob = new Blob([blobData], {type: "application/pdf"});
    const blobUrl = window.URL.createObjectURL(blob);
    // Open a new window with the Blob URL
    window.open(blobUrl, '_blank');
  }

  handleMenuAction(rowData: RowDataStructure, params: ICellRendererParams | ActionItemRendererParams, actionItem: ActionItem, searchAppServiceResponse?: SepAppInfoStructure[], gridApi?: GridApi): void {
    switch(actionItem.id){
      case(APPLICATION_ACTION_OPTIONS.VIEW_DOCUMENTS):
        this.viewDocuments(rowData, searchAppServiceResponse);
        break;
      case(APPLICATION_ACTION_OPTIONS.DOWNLOAD_APPLICATION):
        this.downloadApplication(rowData)
        break;
      case(APPLICATION_ACTION_OPTIONS.VIEW_NOTES):
        this.viewNotes(params);
        break;
      case(APPLICATION_ACTION_OPTIONS.UPLOAD_DOCUMENTS):
        this.uploadDocuments(params, rowData, gridApi);
        break;
    }
  }

  handleQuickAction(rowData: RowDataStructure, params: ActionItemRendererParams | ICellRendererParams, actionItem: ActionItem, searchAppServiceResponse?: SepAppInfoStructure[]): void{
    switch(actionItem.id){
      case(APPLICATION_ACTION_OPTIONS.VIEW_NOTES):
        this.viewNotes(params);
        break;
      case(APPLICATION_ACTION_OPTIONS.VIEW_DOCUMENTS):
        this.viewDocuments(rowData, searchAppServiceResponse)
    }
  } 

  viewNotes(params: ActionItemRendererParams | ICellRendererParams): void{
    const isExpanded = params.node.expanded;
    params.node.setExpanded(!isExpanded);
  }

  async viewDocuments(rowData: RowDataStructure, searchAppServiceResponse: SepAppInfoStructure[]): Promise<any>{
    const pdfObj = await this.viewApplicationDocumentService(searchAppServiceResponse, rowData.applicationNumber);
    this.openPdfInPrintWindow(pdfObj);
  }

  downloadApplication(rowData: RowDataStructure){
    this.onPrintPage(rowData);
  }

  uploadDocuments(params: ActionItemRendererParams | ICellRendererParams, selectedAppData: RowDataStructure, gridApi: GridApi): MatDialogRef<UploadDocumentsModalComponent, ModalArgs>{
    const loginInfo = this.reducerUtilityService.getApplicationStates().LoginInfo;
    const editorName =  `${loginInfo.basicUserInfo.firstName} ${loginInfo.basicUserInfo.lastName}`;
    const modalArgs: ModalArgs = {
      appParams: params,
      appData: selectedAppData,
      existingDocs: selectedAppData.documents.files.map(v => { 
        return { displayableFileObj: v, doctype: v.documentDescription };
      }),
      appGridApi: gridApi,
      editorName: editorName
    }
    return this.matDialog.open(UploadDocumentsModalComponent, {
      maxWidth: "70vw",
      disableClose: true,
      data: modalArgs
    })
  }

  async updateNotesOrStatus( params: DetailNotesRendererParams, newValue: string, saveType: SEP_REVIEW_DASHBOARD_COLUMN_NAME.NOTES | SEP_REVIEW_DASHBOARD_COLUMN_NAME.STATUS, gridApi: GridApi): Promise<any>{
    const loginInfo = this.reducerUtilityService.getApplicationStates().LoginInfo;
    const applicationId = params.node.data.applicationNumber
    const payload = {
      appId: applicationId,
      note: saveType === SEP_REVIEW_DASHBOARD_COLUMN_NAME.NOTES ? newValue : null,
      status: saveType === SEP_REVIEW_DASHBOARD_COLUMN_NAME.STATUS ? newValue : null,
      editorName: `${loginInfo.basicUserInfo.firstName} ${loginInfo.basicUserInfo.lastName}`
    };

    try{
      const serviceResponse: SuccessResponse | HttpErrorResponse = await this.updateNotesAndStatusService(payload, loginInfo.userId);
      if(!(serviceResponse instanceof HttpErrorResponse)){
        this.updateLastEditedData([params.node.data]);
        this.updateData(newValue, applicationId, saveType, gridApi);
        this.matSnackBar.open(`Changes saved!`, 'OK', {
          duration: 1000,
          panelClass: 'positive-panel-class'
        });
      }
    }catch(error){
      this.matSnackBar.open(`We had a problem saving your changes. Please try again later.`, 'OK', {
        duration: 1000,
        panelClass: 'negative-panel-class'
      });
      console.error("Error updating note or status");
    }

  }

  async updateNotesAndStatusService(payloadData: AddNoteServicePayload, userId: string): Promise<HttpErrorResponse | SuccessResponse>{

    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: `/sxesvc/api/v1/quote/addNote`,
      headers: new HttpHeaders({
        ddToken: this.brandService.getCurrentBrand().ddToken_encoded(),
        fromDP: userId
      }),
      POST_OR_PUT_PAYLOAD: {
        payload: payloadData
      }
    });
  
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest<HttpErrorResponse | SuccessResponse>(requestConfig).catch((fail) => fail);
  }

  buildDetailDisplayObject(appInfo: SepAppInfoStructure): DetailObject[]{
      let dependentArray: DetailValues[] = [
        {
          label: 'N/A'
        }
      ];
      if(appInfo.dependents?.length > 0){
          dependentArray = 
            [
              { 
                isSubheader: true,
                label: "Name",
                value: "Relationship"
              }
            ];
        for(const dep of appInfo.dependents){
          dependentArray.push({
            label: dep.name,
            value: RELATIONSHIP_TO_APPLICANT[dep.relationship]
          });
        }
      }
      let detailValueObj: DetailObject[] = [
        { sectionHeader: 'Dependents', 
          detailDisplayValues:{
            details: dependentArray
          }
        },
        { 
          sectionHeader: 'Applicant Contact Information', 
            detailDisplayValues:{
              details: [
                { 
                  isSubheader: true,
                  label: "Applicant Name"
                },
                {
                  label: `${appInfo.subscriberFirstName} ${appInfo.subscriberLastName}`
                },
                { 
                  isSubheader: true,
                  label: "Applicant Phone Number"
                },
                {
                  label: appInfo.applicantPhoneNumber ?? 'N/A'
                },
                {
                  isSubheader: true,
                  label: "Applicant Email"
                },
                {
                  label: appInfo.subscriberEmail ?? 'N/A'
                }
            ]}
        },
        { 
          sectionHeader: 'Agent Contact Information', 
            detailDisplayValues:{
              details: [
                { isSubheader: true,
                  label: "Agent Name"
                },
                {
                  label: appInfo.agentInfo?.agentName ?? 'N/A'
                },
                { isSubheader: true,
                  label: "Agent Phone Number"
                },                
                {
                  label: appInfo.agentInfo?.agentPhoneNumber ?? 'N/A'
                },
                { isSubheader: true,
                  label: "Agent Email"
                },
                {
                  label: appInfo.agentInfo?.agentEmail ?? 'N/A'
                },
            ]}
          }
      ];

      return detailValueObj;
  }

  getDocumentsArray(appData: SepAppInfoStructure): DocumentStore{
    let documentsArray: DisplayableFile[] = [];
    if(appData.documents?.documentId && appData.documents?.fileObjs){
      for(const file of appData.documents.fileObjs){
        const displayableFile: DisplayableFile = {
          name: file.name,
          size: file.size,
          status: FILE_UPLOAD_STATUS.SUCCESS,
          documentDescription: file.description
        }

        documentsArray.push(displayableFile);
      }
    }
    return {
      documentId: appData.documents?.documentId ?? null,
      files: documentsArray
    };
  }

  getLastNote(appData: SepAppInfoStructure): string{
    let lastNote: string = null;
    for(const noteObj of appData.notes){
      if(noteObj.note != null){
        lastNote = noteObj.note;
      }
    }
    return lastNote;
  }

  updateData(newValue: any, applicationNumber: string, updateField: SEP_REVIEW_DASHBOARD_COLUMN_NAME, gridApi: GridApi): void{
    const rowNode = gridApi.getRowNode(applicationNumber);

    const updateFilter = {};
    updateFilter[updateField] = newValue
  
    if (rowNode) {
      const updatedRowData = {
        ...rowNode.data,
        [updateField]: newValue
      };

      // Apply transaction instead of updateData()
      gridApi.applyTransaction({ update: [updatedRowData] });
      gridApi.refreshCells({force: true});
    }
  }

  async postDocumentUpload(documentArray: DisplayableFile[], params: ActionItemRendererParams | ICellRendererParams, appData: RowDataStructure, gridApi: GridApi){
    const enrollmentObj: EnrollPayloadRequest = await this.fetchEnrollmentObjectByAppId(appData.applicationNumber);
    
    enrollmentObj.enrollment.sepInformation.documentUploadType = this.getDocumentUploadTypes(documentArray);

    enrollmentObj.applicationTrackingId = appData.applicationNumber;
    const submitResponse: SubmitAppResponse | HttpErrorResponse = await this.individualMarketsEnrollmentSubmissionService.submit(enrollmentObj);
    if(submitResponse instanceof HttpErrorResponse){
      this.matSnackBar.open("There was a problem uploading your documents", 'OK', {
        duration: 5000,
        panelClass: 'negative-panel-class'
      });
    } else {
      this.matSnackBar.open("Documents uploaded!", 'OK', {
        duration: 5000,
        panelClass: 'positive-panel-class'
      });
      this.updateData(documentArray, appData.applicationNumber, SEP_REVIEW_DASHBOARD_COLUMN_NAME.DOCUMENTS, gridApi);
      this.updateLastEditedData([params.node.data]);
    }
  }
  
  async onPrintPage(rowData: RowDataStructure): Promise<void> {
    
    // Fetch enrollment object
    const enrollmentObj: EnrollPayloadRequest
      = await this.fetchEnrollmentObjectByAppId(rowData.applicationNumber);

    const productAttributes = enrollmentObj.enrollment.productData.planDetails?.attributes;

    let region: string;
    let productGroup: string;

    if (productAttributes) {
      region = productAttributes.PLAN_REGION?.[0];
      productGroup = productAttributes.PRODUCT_GROUP;
    }
        
    // Fetch the PDF with region and product group information
    const response = await this.printButtonService.fetchSubmittedApplicationPDF({
      appId: rowData.applicationNumber,
      region: region,
      productGroup: productGroup,
      subGroup: PRINT_TYPE.APPLICATION_FORM
    });
    
    this.openPdfInPrintWindow(response);
  }
  
  // This has duplicatish functionality in the enrollment-submission.individual-markets.service.ts, but ran out of time to sync
  getDocumentUploadTypes(documentArray: DisplayableFile[]): DOCUMENT_UPLOAD_TYPES[] {
    /** Add reference to the TYPE of documents uploaded, for use in print pdf */
    const docUploadTypes: DOCUMENT_UPLOAD_TYPES[] = [];
    documentArray.forEach(d => {
      const docDescription = d.documentDescription;
      if (docDescription) {
        const type = DOCUMENT_UPLOAD_TYPES[docDescription];
        if (type) {
          docUploadTypes.push(type);
        }
      }
    });

    return Array.from(new Set(docUploadTypes)); // unique
  }

  // moved into service and out of grid callback to cut down on cluttering the grid with service logic
  async submitApplication(params: ActionItemRendererParams | ICellRendererParams, rowData: RowDataStructure, gridApi: GridApi){
    // Fetch enrollment object
    const enrollmentObj: EnrollPayloadRequest
      = JSON.parse(rowData.enrollmentObj);
    
    // Update status
    enrollmentObj.submitStatus = APP_STATUS.SUBMITTED;
    enrollmentObj.applicationTrackingId = rowData.applicationNumber;

    // Set document upload types
    enrollmentObj.enrollment.sepInformation.documentUploadType = this.getDocumentUploadTypes(rowData.documents.files);

    enrollmentObj.enrollment.sepInformation.editorName = rowData.lastEditedBy;

    // Submit
    const submitResponse = await this.individualMarketsEnrollmentSubmissionService.submit(enrollmentObj);

    if(!(submitResponse instanceof HttpErrorResponse)){
      this.matSnackBar.open(`Application submitted successfully!`, 'OK', {
        duration: 5000,
        panelClass: 'positive-panel-class'
      });
      // Update Grid if app is submitted successfully
      rowData.status = STATUS_OPTION_SERVICE_VALUES.SUBMITTED;
      this.gridApi.applyTransaction({update: [rowData]});
      this.updateLastEditedData([params.node.data]);
    } else {
      this.matSnackBar.open(`There was an error submitting your selected application`, 'OK', {
        duration: 5000,
        panelClass: 'negative-panel-class'
      });
    }
  }

  buildBulkSubmitPayload(selectedRows: RowDataStructure[]): EnrollPayloadRequest[]{
    /** bulk submit payload must be an array of enrollment objects
     * they are parsed into JSON objects in order to edit the data 
     * and add them to the array for submission */
    let appsForSubmit: EnrollPayloadRequest[] = [];

    for(const row of selectedRows){
      const enrollObj = JSON.parse(row.enrollmentObj);
      // Update staus
      enrollObj.submitStatus = APP_STATUS.SUBMITTED;
      enrollObj.applicationTrackingId = row.applicationNumber;
  
      // Set document upload types
      enrollObj.enrollment.sepInformation.documentUploadType = this.getDocumentUploadTypes(row.documents.files);
      enrollObj.enrollment.sepInformation.editorName = row.lastEditedBy;

      appsForSubmit.push(enrollObj);
    }
      return appsForSubmit;
  }

  async triggerBulkSubmit(){
    const selectedApps = this.gridApi.getSelectedRows();
    if(selectedApps.length > 100){
      this.ptlNotificationService.open({
        title: 'Too Many Applications Selected',
        content: 'Bulk submission is limited to a maximum of 100 applications. Please adjust the number of selected applications and try again.',
        primaryButtonText: 'OK',
        notificationStyle: PtlNotificationStyle.DANGER,
        modalWidth: '440px'
      });
    } else {
      const bulkSubmitPayload = this.buildBulkSubmitPayload(selectedApps);
      const submitResponse: BulkSubmitResponse[] | HttpErrorResponse = await this.bulkSubmit(bulkSubmitPayload);

      if(!(submitResponse instanceof HttpErrorResponse)) {
        this.matSnackBar.open(`Applications submitted successfully!`, 'OK', {
          duration: 5000,
          panelClass: 'positive-panel-class'
        });
        //update row data in grid to reflect that applications were submitted
        let appsToUpdate = [];
        for(const app of selectedApps){
          app.status = STATUS_OPTION_SERVICE_VALUES.SUBMITTED;
          appsToUpdate.push(app);
        }
        this.gridApi.applyTransaction({update: appsToUpdate});
        this.updateLastEditedData(appsToUpdate);
      } else {
        this.matSnackBar.open(`There was an error submitting your selected applications`, 'OK', {
          duration: 5000,
          panelClass: 'negative-panel-class'
        });
      }
    }
  }

  async bulkSubmit(enrollmentObjArray: EnrollPayloadRequest[]): Promise<BulkSubmitResponse[] | HttpErrorResponse>{
    const loginInfo: UserInfoStateStructure = this.reducerUtilityService.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);

    const requestConfig = new RequestConfigImplementation({
      url: '/sales-portal-services/v1/submitApp/bulkSubmit',
      method: REQUEST_METHODS.POST,
      headers: new HttpHeaders({
        fromDP: loginInfo.userId
      }),
      POST_OR_PUT_PAYLOAD: enrollmentObjArray
    });
      
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);

    const response: BulkSubmitResponse[] | HttpErrorResponse
    = await this.httpClient
    .makeRequest<BulkSubmitResponse[]>(requestConfig)
    .catch( (error: HttpErrorResponse) => {
      return error;
    });

    return response;
  }

  updateLastEditedData(appData: RowDataStructure[]) { // repeat for any updates made to the grid
    const loginInfo = this.reducerUtilityService.getApplicationStates().LoginInfo;
    const lastModifiedByValue = `${loginInfo.basicUserInfo.firstName} ${loginInfo.basicUserInfo.lastName}`
    const lastModifiedTimeValue = moment()
    for(const app of appData){
      const applicationId = app.applicationNumber;
      this.updateData(lastModifiedByValue, applicationId, SEP_REVIEW_DASHBOARD_COLUMN_NAME.LAST_EDITED_BY, this.gridApi);
      this.updateData(lastModifiedTimeValue, applicationId, SEP_REVIEW_DASHBOARD_COLUMN_NAME.LAST_EDITED, this.gridApi);
    }
  }
}

